import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Check, Crown, Zap, Star, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import './PricingPage.css';

export default function PricingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [duration, setDuration] = useState(1);

  const pricing = {
    basic: { 1: 4.99, 3: 13.49, 6: 24.99, 12: 49.99 },
    premium: { 1: 14.90, 3: 39.90, 6: 74.90, 12: 139.90 }
  };

  const getMonthlyPrice = (tier, months) => (pricing[tier][months] / months).toFixed(2);
  const getSavings = (tier, months) => {
    if (months === 1) return 0;
    const fullPrice = pricing[tier][1] * months;
    return Math.round(((fullPrice - pricing[tier][months]) / fullPrice) * 100);
  };

  const plans = [
    {
      id: 'free',
      name: t('pricing.free_title'),
      icon: <Star size={32} />,
      price: '0',
      color: '#6b7280',
      features: [
        t('pricing.free_feature1'),
        t('pricing.free_feature2'),
        t('pricing.free_feature3'),
        t('pricing.free_feature4'),
        t('pricing.free_feature5')
      ],
      limitations: [
        t('pricing.free_limit1'),
        t('pricing.free_limit2')
      ]
    },
    {
      id: 'basic',
      name: t('pricing.basic_title'),
      icon: <Zap size={32} />,
      color: '#3b82f6',
      popular: true,
      features: [
        t('pricing.basic_feature1'),
        t('pricing.basic_feature2'),
        t('pricing.basic_feature3'),
        t('pricing.basic_feature4'),
        t('pricing.basic_feature5')
      ]
    },
    {
      id: 'premium',
      name: t('pricing.premium_title'),
      icon: <Crown size={32} />,
      color: '#f59e0b',
      features: [
        t('pricing.premium_feature1'),
        t('pricing.premium_feature2'),
        t('pricing.premium_feature3'),
        t('pricing.premium_feature4'),
        t('pricing.premium_feature5'),
        t('pricing.premium_feature6')
      ]
    }
  ];

  return (
    <div className="pricing-page">
      <header className="pricing-header">
        <h1>{t('pricing.title')}</h1>
        <p className="pricing-subtitle">{t('pricing.subtitle')}</p>
      </header>

      <div className="duration-toggle">
        <button className={duration === 1 ? 'active' : ''} onClick={() => setDuration(1)}>
          1 {t('pricing.month')}
        </button>
        <button className={duration === 3 ? 'active' : ''} onClick={() => setDuration(3)}>
          3 {t('pricing.months')}
          {getSavings('basic', 3) > 0 && <span className="badge">-{getSavings('basic', 3)}%</span>}
        </button>
        <button className={duration === 6 ? 'active' : ''} onClick={() => setDuration(6)}>
          6 {t('pricing.months')}
          {getSavings('basic', 6) > 0 && <span className="badge">-{getSavings('basic', 6)}%</span>}
        </button>
        <button className={duration === 12 ? 'active' : ''} onClick={() => setDuration(12)}>
          1 {t('pricing.year')}
          {getSavings('premium', 12) > 0 && <span className="badge popular">-{getSavings('premium', 12)}%</span>}
        </button>
      </div>

      <div className="pricing-grid">
        {plans.map((plan) => (
          <div key={plan.id} className={`pricing-card ${plan.popular ? 'popular' : ''}`} style={{ borderTopColor: plan.color }}>
            {plan.popular && <div className="popular-badge">{t('pricing.most_popular')}</div>}
            
            <div className="plan-icon" style={{ color: plan.color }}>{plan.icon}</div>
            <h3 className="plan-name">{plan.name}</h3>

            <div className="plan-price">
              {plan.id === 'free' ? (
                <>
                  <span className="price">€0</span>
                  <span className="period">/{t('pricing.forever')}</span>
                </>
              ) : (
                <>
                  <span className="price">€{pricing[plan.id][duration]}</span>
                  <span className="period">/{duration} {duration === 1 ? t('pricing.month') : t('pricing.months')}</span>
                  <div className="monthly-price">≈ €{getMonthlyPrice(plan.id, duration)}/{t('pricing.month')}</div>
                </>
              )}
            </div>

            <ul className="plan-features">
              {plan.features.map((feature, idx) => (
                <li key={idx}><Check size={18} /><span>{feature}</span></li>
              ))}
              {plan.limitations && plan.limitations.map((limit, idx) => (
                <li key={`limit-${idx}`} className="limitation"><X size={18} /><span>{limit}</span></li>
              ))}
            </ul>

            {plan.id === 'free' ? (
              <button className="btn-plan" disabled>{t('pricing.current_plan')}</button>
            ) : (
              <button className="btn-plan" style={{ background: plan.color }} onClick={() => navigate('/login')}>
                {t('pricing.select_plan')}
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="faq-section">
        <h2>{t('pricing.faq_title')}</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h4>{t('pricing.faq1_q')}</h4>
            <p>{t('pricing.faq1_a')}</p>
          </div>
          <div className="faq-item">
            <h4>{t('pricing.faq2_q')}</h4>
            <p>{t('pricing.faq2_a')}</p>
          </div>
          <div className="faq-item">
            <h4>{t('pricing.faq3_q')}</h4>
            <p>{t('pricing.faq3_a')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
